import React, {
  useCallback, useLayoutEffect, useRef, useState, useSyncExternalStore,
} from 'react';
import { createPortal } from 'react-dom';
import {
  subscribeVariableSuggestion,
  getVariableSuggestionFor,
  setVariableSuggestionSelection,
  setVariableSuggestionSearch,
  moveVariableSuggestionSelection,
  commitVariableSuggestionSelection,
  closeVariableSuggestion,
  VARIABLE_SUGGESTION_LIMIT,
} from '../utils/variableSuggestionRegistry';
import {
  subscribeVariableCatalog,
  getVariableCatalogSnapshot,
} from '../utils/variableCatalogRegistry';
import logo from '../../images/symbol.svg';
import classes from './VariableSuggestionMenu.module.css';

/**
 * Menu déroulant du déclencheur `@`.
 *
 * Reprend la présentation du menu de l'éditeur classique
 * (`components/ContextualMenu`) : champ de recherche en tête, liste à plat de
 * lignes « Groupe / Nom », logo pour les variables venant de l'application.
 *
 * Monté dans chaque éditeur rich text mais **rendu par un seul** : le registre
 * ne renvoie un état qu'au propriétaire du menu ouvert, les autres lisent une
 * référence `null` stable et ne se re-rendent jamais (invariants §4 du README).
 *
 * Le menu est porté dans le `body` du document de l'éditeur — le canvas Puck
 * est une iframe, y compris pour le positionnement `fixed`.
 */
const MENU_GAP = 6;

/** Une variable personnalisée n'est pas issue de l'application : pas de logo. */
const isCustomVariable = (key) => String(key || '').startsWith('custom.');

/* Liste vide : dire pourquoi. « Aucun document rattaché » vise l'éditeur de
   template web, dont le catalogue n'a aucune source. */
const EMPTY_MESSAGES = {
  error: 'Variables indisponibles — le chargement a échoué.',
  unavailable: 'Aucun document rattaché : pas de variables ici.',
};

export default function VariableSuggestionMenu({ editor }) {
  const subscribe = useCallback((listener) => subscribeVariableSuggestion(listener), []);
  const getSnapshot = useCallback(() => getVariableSuggestionFor(editor), [editor]);
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  // Statut du catalogue : une liste vide parce que la requête a échoué ne doit
  // pas se lire comme « ce document n'a pas de variables ».
  const catalogState = useSyncExternalStore(
    subscribeVariableCatalog,
    getVariableCatalogSnapshot,
    getVariableCatalogSnapshot,
  );

  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const [position, setPosition] = useState(null);

  const anchorRect = state?.clientRect?.() || null;
  const anchorKey = anchorRect
    ? `${Math.round(anchorRect.left)}:${Math.round(anchorRect.bottom)}`
    : null;

  // Position calculée après mesure : sous le curseur, au-dessus s'il n'y a pas
  // la place — un menu qui déborde vers le bas est inatteignable dans l'iframe.
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu || !anchorRect) {
      setPosition(null);
      return;
    }
    const view = menu.ownerDocument.defaultView;
    const height = menu.offsetHeight;
    const width = menu.offsetWidth;
    const openUpwards = anchorRect.bottom + MENU_GAP + height > view.innerHeight
      && anchorRect.top - MENU_GAP - height > 0;
    setPosition({
      top: openUpwards
        ? anchorRect.top - MENU_GAP - height
        : anchorRect.bottom + MENU_GAP,
      left: Math.max(8, Math.min(anchorRect.left, view.innerWidth - width - 8)),
    });
    // anchorKey suffit : recalculer à chaque frappe sur la même position est inutile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchorKey, state?.items?.length]);

  const selectedRef = useRef(null);
  useLayoutEffect(() => {
    // `?.` sur la méthode aussi : jsdom ne l'implémente pas.
    selectedRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [state?.selectedIndex]);

  // Le champ de recherche prend le focus : la navigation clavier de l'éditeur
  // ne reçoit plus les touches, ce menu les traite donc lui-même.
  const onSearchKeyDown = (event) => {
    // Puck écoute les raccourcis sur le `document` du canvas et annule la touche
    // quand elle déclenche une action — `Backspace` supprime le bloc
    // sélectionné. Sans cette coupure, on ne peut rien effacer dans le champ.
    event.stopPropagation();
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      moveVariableSuggestionSelection(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      commitVariableSuggestionSelection();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      closeVariableSuggestion(editor);
      editor.commands.focus();
    }
  };

  if (!state || !editor) return null;

  const doc = editor.view?.dom?.ownerDocument;
  if (!doc?.body) return null;

  const { items, selectedIndex, search } = state;

  const emptyMessage = EMPTY_MESSAGES[catalogState.status] || 'Aucune variable ne correspond';

  const menu = (
    <div
      ref={menuRef}
      className={classes.menu}
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        visibility: position ? 'visible' : 'hidden',
      }}
      // La frappe reste dans l'éditeur : le menu ne doit jamais lui voler le
      // focus — sauf le champ de recherche, qui a besoin de le prendre.
      onMouseDown={(event) => {
        if (event.target !== searchInputRef.current) event.preventDefault();
      }}
    >
      <div className={classes.searchRow}>
        <input
          ref={searchInputRef}
          className={classes.search}
          type="text"
          placeholder="Rechercher une variable"
          value={search}
          onChange={(event) => setVariableSuggestionSearch(event.target.value)}
          onKeyDown={onSearchKeyDown}
          // Puck relâche ses touches sur `keyup` : le laisser passer suffirait
          // à désynchroniser son état de raccourcis, autant tout couper.
          onKeyUp={(event) => event.stopPropagation()}
        />
      </div>
      {items.length === 0 ? (
        <div className={classes.empty}>{emptyMessage}</div>
      ) : (
        <ul className={classes.list} role="listbox" aria-label="Variables">
          {items.map((variable, index) => {
            const selected = index === selectedIndex;
            return (
              <li
                key={variable.key}
                ref={selected ? selectedRef : null}
                role="option"
                aria-selected={selected}
                className={`${classes.option} ${selected ? classes.optionSelected : ''}`.trim()}
                onMouseMove={() => setVariableSuggestionSelection(index)}
                onClick={() => commitVariableSuggestionSelection()}
              >
                <span className={classes.optionIcon}>
                  {isCustomVariable(variable.key) ? null : (
                    <img src={logo} alt="" width={20} height={20} />
                  )}
                </span>
                <span className={classes.optionName}>
                  <span className={classes.optionGroup}>{variable.groupLabel} / </span>
                  {variable.name}
                </span>
                {variable.value ? (
                  <span className={classes.optionValue}>{variable.value}</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {items.length >= VARIABLE_SUGGESTION_LIMIT && (
        <div className={classes.footer}>
          Liste tronquée — précisez votre recherche
        </div>
      )}
    </div>
  );

  return createPortal(menu, doc.body);
}
